import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useAppStore from '@/store';
import styles from './uploadManager.module.scss';
import { useTranslation } from 'react-i18next';
import type { UploadTask } from '@/types';
import ChevronUpIcon from '@/images/chevron-up.svg?react';
import XMarkIcon from '@/images/x-mark.svg?react';

export const UploadManager: React.FC = () => {
  const uploadTasks = useAppStore((state) => state.ui.uploadTasks);
  const removeUploadTask = useAppStore((state) => state.removeUploadTask);
  const cancelUploadTask = useAppStore((state) => state.cancelUploadTask);
  const hasActiveTasks = uploadTasks.some(
    (upload) => upload.status === 'uploading' || upload.status === 'processing'
  );
  const scheduledForRemoval = useRef<Set<string>>(new Set());
  const [isCollapsed, setIsCollapsed] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    if (hasActiveTasks) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasActiveTasks]);

  useEffect(() => {
    const completedTasks = uploadTasks.filter(
      (task) =>
        (task.status === 'completed' || task.status === 'cancelled') &&
        !scheduledForRemoval.current.has(task.id)
    );

    completedTasks.forEach((task) => {
      scheduledForRemoval.current.add(task.id);

      setTimeout(() => {
        removeUploadTask(task.id);
        scheduledForRemoval.current.delete(task.id);
      }, 5000);
    });

    const currentTaskIds = new Set(uploadTasks.map((task) => task.id));
    scheduledForRemoval.current.forEach((id) => {
      if (!currentTaskIds.has(id)) {
        scheduledForRemoval.current.delete(id);
      }
    });
  }, [uploadTasks, removeUploadTask]);

  if (uploadTasks.length === 0) return null;

  const portalContainer = document.getElementById('upload-portal');
  if (!portalContainer) return null;

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const shouldShowRemoveButton = (uploadTask: UploadTask) => {
    return (
      uploadTask.status === 'completed' ||
      uploadTask.status === 'error' ||
      uploadTask.status === 'cancelled'
    );
  };

  const shouldShowCancelButton = (uploadTask: UploadTask) => {
    return (
      (uploadTask.status === 'preparing' ||
        uploadTask.status === 'uploading') &&
      uploadTask.controller !== undefined
    );
  };

  const content = (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <span className={styles.headerTitle}>
            {t('uploadManager.title')} ({uploadTasks.length})
          </span>
          <button onClick={toggleCollapse} className={styles.collapseButton}>
            <ChevronUpIcon
              className={`${styles.collapseIcon} ${isCollapsed ? styles.rotated : ''}`}
            />
          </button>
        </div>
      </div>

      <div
        className={`${styles.content} ${isCollapsed ? styles.contentCollapsed : ''}`}
      >
        {uploadTasks.map((upload) => (
          <div key={upload.id} className={styles.uploadItem}>
            <div className={styles.uploadContent}>
              <div className={styles.uploadHeader}>
                <p className={styles.fileName} title={upload.name}>
                  {upload.name}
                </p>
                <div className={styles.statusContainer}>
                  <div className={styles.statusGroup}>
                    {upload.status === 'preparing' && (
                      <span
                        className={`${styles.statusText} ${styles.preparing}`}
                      >
                        {t('uploadManager.preparing')}
                      </span>
                    )}
                    {upload.status === 'uploading' && (
                      <span
                        className={`${styles.statusText} ${styles.uploading}`}
                      >
                        {t('uploadManager.uploading')}
                      </span>
                    )}
                    {upload.status === 'processing' && (
                      <span
                        className={`${styles.statusText} ${styles.processing}`}
                      >
                        {t('uploadManager.processing')}
                      </span>
                    )}
                    {upload.status === 'completed' && (
                      <span
                        className={`${styles.statusText} ${styles.completed}`}
                      >
                        {t('uploadManager.completed')}
                      </span>
                    )}
                    {upload.status === 'cancelled' && (
                      <span
                        className={`${styles.statusText} ${styles.cancelled}`}
                      >
                        {t('uploadManager.cancelled')}
                      </span>
                    )}
                    {upload.status === 'error' && (
                      <span className={`${styles.statusText} ${styles.error}`}>
                        {t('uploadManager.error')}
                      </span>
                    )}
                  </div>
                  {(shouldShowCancelButton(upload) ||
                    shouldShowRemoveButton(upload)) && (
                    <button
                      onClick={() =>
                        shouldShowCancelButton(upload)
                          ? cancelUploadTask(upload.id)
                          : removeUploadTask(upload.id)
                      }
                      className={styles.removeButton}
                      title={
                        shouldShowCancelButton(upload)
                          ? t('uploadManager.cancel')
                          : t('uploadManager.remove')
                      }
                    >
                      <XMarkIcon />
                    </button>
                  )}
                </div>
              </div>

              {upload.status === 'uploading' && (
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{ width: `${upload.progress}%` }}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return createPortal(content, portalContainer);
};
